import { type FC, useState, useCallback, useRef, useEffect } from 'react';
import type { VariantL10n } from '../../services/data-review';
import { useUpdateVariantField } from '../../hooks/data-review';
import { Card } from '../atoms/Card';

interface ProductVariantCardProps {
  variant: VariantL10n;
  locale: string;
  differentFields?: Set<string>;
}

interface EditableFieldProps {
  label: string;
  value: string | number | boolean | null;
  fieldName: string;
  variantId: number;
  locale: string;
  type?: 'text' | 'textarea' | 'number' | 'boolean';
  isDifferent?: boolean;
}

const EditableField: FC<EditableFieldProps> = ({
  label,
  value,
  fieldName,
  variantId,
  locale,
  type = 'text',
  isDifferent = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() ?? '');
  const [originalValue, setOriginalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);
  const updateField = useUpdateVariantField();
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    setEditValue(value?.toString() ?? '');
    setOriginalValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    // Prevent duplicate updates
    if (isUpdatingRef.current) {
      return;
    }

    let processedValue: string | number | boolean | null = editValue;

    if (type === 'number') {
      processedValue = editValue ? Number(editValue) : null;
    } else if (type === 'boolean') {
      processedValue = editValue === 'true';
    } else if (editValue === '') {
      processedValue = null;
    }

    // Check if value actually changed
    const hasChanged = type === 'number'
      ? Number(processedValue) !== Number(originalValue)
      : processedValue !== originalValue;

    if (!hasChanged) {
      setIsEditing(false);
      return;
    }

    isUpdatingRef.current = true;

    updateField.mutate({
      variantId,
      locale,
      field: fieldName,
      value: processedValue
    }, {
      onSuccess: () => {
        setIsEditing(false);
        setOriginalValue(processedValue);
        isUpdatingRef.current = false;
      },
      onError: () => {
        isUpdatingRef.current = false;
      }
    });
  }, [variantId, locale, fieldName, editValue, type, updateField, originalValue]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value?.toString() ?? '');
      setIsEditing(false);
    }
  }, [value, handleSave]);

  const displayValue = type === 'boolean'
    ? (value ? 'Yes' : 'No')
    : (value?.toString() ?? '-');

  return (
    <div className={`border-b border-gray-200 py-2 ${isDifferent ? 'bg-yellow-50 border-l-4 border-l-yellow-400 pl-2' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <span className="text-sm font-medium text-gray-700">{label}:</span>
          {isEditing ? (
            <div className="mt-1">
              {type === 'textarea' ? (
                <textarea
                  ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSave}
                  className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  rows={3}
                  disabled={updateField.isPending}
                />
              ) : type === 'boolean' ? (
                <select
                  ref={inputRef as React.RefObject<HTMLSelectElement>}
                  value={editValue}
                  onChange={(e) => {
                    setEditValue(e.target.value);
                    setTimeout(() => {
                      const boolValue = e.target.value === 'true';
                      updateField.mutate({
                        variantId,
                        locale,
                        field: fieldName,
                        value: boolValue
                      }, {
                        onSuccess: () => setIsEditing(false)
                      });
                    }, 0);
                  }}
                  onBlur={() => setIsEditing(false)}
                  className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  disabled={updateField.isPending}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : (
                <input
                  ref={inputRef as React.RefObject<HTMLInputElement>}
                  type={type}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSave}
                  className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  disabled={updateField.isPending}
                />
              )}
            </div>
          ) : (
            <p
              onClick={() => setIsEditing(true)}
              className="text-sm text-gray-900 mt-1 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors"
            >
              {displayValue}
            </p>
          )}
        </div>
        {updateField.isPending && (
          <div className="flex items-center text-xs text-blue-600">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export const ProductVariantCard: FC<ProductVariantCardProps> = ({ variant, locale, differentFields }) => {
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      setPreviewPosition({
        x: rect.right + 16,
        y: rect.top
      });
      setShowImagePreview(true);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowImagePreview(false);
  }, []);

  return (
    <Card className="p-4">
      <div className="flex gap-4">
        {/* Product Image */}
        <div className="flex-shrink-0">
          {variant.imageUrl ? (
            <>
              <img
                ref={imageRef}
                src={variant.imageUrl}
                alt={variant.name}
                className="w-32 h-32 object-cover rounded-lg cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
              {showImagePreview && (
                <div
                  className="fixed z-[9999] pointer-events-none"
                  style={{
                    left: `${previewPosition.x}px`,
                    top: `${previewPosition.y}px`
                  }}
                >
                  <div className="bg-white rounded-lg shadow-2xl border-2 border-gray-300 p-2">
                    <img
                      src={variant.imageUrl}
                      alt={`${variant.name} preview`}
                      className="w-96 h-96 object-contain rounded"
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
              <span className="text-gray-400 text-sm">No image</span>
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className="flex-1 space-y-1">
          <div className="mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{variant.name}</h3>
          </div>

          {/* Basic Info (Read-only) */}
          <div className="grid grid-cols-2 gap-x-4">
            <div className="border-b border-gray-200 py-2">
              <span className="text-sm font-medium text-gray-700">SKU:</span>
              <p className="text-sm text-gray-900 mt-1 px-2 py-1">{variant.sku}</p>
            </div>
            <EditableField label="Product Type" value={variant.productType} fieldName="productType" variantId={variant.variantId} locale={locale} isDifferent={differentFields?.has('productType')} />
            <div className="border-b border-gray-200 py-2">
              <span className="text-sm font-medium text-gray-700">Family ID:</span>
              <p className="text-sm text-gray-900 mt-1 px-2 py-1">{variant.familyId}</p>
            </div>
            <div className="border-b border-gray-200 py-2">
              <span className="text-sm font-medium text-gray-700">Variant ID:</span>
              <p className="text-sm text-gray-900 mt-1 px-2 py-1">{variant.variantId}</p>
            </div>
          </div>

          {/* Editable Fields */}
          <EditableField label="Name" value={variant.name} fieldName="name" variantId={variant.variantId} locale={locale} isDifferent={differentFields?.has('name')} />
          <EditableField label="Description" value={variant.description} fieldName="description" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('description')} />
          <EditableField label="Measurement Unit" value={variant.measurementUnit} fieldName="measurementUnit" variantId={variant.variantId} locale={locale} isDifferent={differentFields?.has('measurementUnit')} />
          <div className="border-b border-gray-200 py-2">
            <span className="text-sm font-medium text-gray-700">Image URL:</span>
            <p className="text-sm text-gray-900 mt-1 px-2 py-1 break-all">{variant.imageUrl || '-'}</p>
          </div>

          {/* Dosage Info */}
          <div className="grid grid-cols-2 gap-x-4">
            <EditableField label="User Daily Dose" value={variant.userDailyDose} fieldName="userDailyDose" variantId={variant.variantId} locale={locale} type="number" isDifferent={differentFields?.has('userDailyDose')} />
            <EditableField label="Capacity" value={variant.capacity} fieldName="capacity" variantId={variant.variantId} locale={locale} type="number" isDifferent={differentFields?.has('capacity')} />
            <EditableField label="Maximum Daily Dose" value={variant.maximumDailyDose} fieldName="maximumDailyDose" variantId={variant.variantId} locale={locale} type="number" isDifferent={differentFields?.has('maximumDailyDose')} />
            <EditableField label="Max Dose Unit" value={variant.maximumDailyDoseUnit} fieldName="maximumDailyDoseUnit" variantId={variant.variantId} locale={locale} isDifferent={differentFields?.has('maximumDailyDoseUnit')} />
          </div>

          {/* Additional Info */}
          <EditableField label="Packaging" value={variant.packaging} fieldName="packaging" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('packaging')} />
          <EditableField label="Benefits" value={variant.benefits} fieldName="benefits" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('benefits')} />
          <EditableField label="Effects" value={variant.effects} fieldName="effects" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('effects')} />
          <EditableField label="Intended Purpose" value={variant.intendedPurpose} fieldName="intendedPurpose" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('intendedPurpose')} />
          <EditableField label="Ingredients" value={variant.ingredients} fieldName="ingredients" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('ingredients')} />
          <EditableField label="Usage Instructions" value={variant.usageInstructions} fieldName="usageInstructions" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('usageInstructions')} />
          <EditableField label="Storage Requirements" value={variant.storageRequirements} fieldName="storageRequirements" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('storageRequirements')} />
          <EditableField label="Nutritional Facts" value={variant.nutritionalFacts} fieldName="nutritionalFacts" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('nutritionalFacts')} />
          <EditableField label="Time Spacing Requirements" value={variant.timeSpacingRequirements} fieldName="timeSpacingRequirements" variantId={variant.variantId} locale={locale} type="textarea" isDifferent={differentFields?.has('timeSpacingRequirements')} />

          {/* Flags */}
          <div className="grid grid-cols-3 gap-x-4">
            <EditableField label="Is Active" value={variant.isActive} fieldName="isActive" variantId={variant.variantId} locale={locale} type="boolean" isDifferent={differentFields?.has('isActive')} />
            <EditableField label="Is Promo Item" value={variant.isPromoItem} fieldName="isPromoItem" variantId={variant.variantId} locale={locale} type="boolean" isDifferent={differentFields?.has('isPromoItem')} />
            <EditableField label="Is Consumable" value={variant.isConsumable} fieldName="isConsumable" variantId={variant.variantId} locale={locale} type="boolean" isDifferent={differentFields?.has('isConsumable')} />
          </div>

          {/* Minimum Restock Threshold */}
          <EditableField label="Min Restock Threshold" value={variant.minimumRestockThreshold} fieldName="minimumRestockThreshold" variantId={variant.variantId} locale={locale} type="number" isDifferent={differentFields?.has('minimumRestockThreshold')} />
        </div>
      </div>
    </Card>
  );
};
